'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems = [
    { label: 'Home', href: '/homepage' },
    { label: 'AI Change Leadership', href: '/ai-change-leadership-pillar-page' },
    { label: 'Assessment and Recognition', href: '/services' },
    { label: 'About', href: '/about' },
    { label: 'Resources', href: '/resources' },
  ];

  const adminSubItems = [
    { label: 'Admin Dashboard', href: '/admin-dashboard' },
    { label: 'Consultation Results', href: '/consultation-results-recording-system' },
    { label: 'Proposal Workflow', href: '/proposal-preparation-workflow' },
    { label: 'Document Generation', href: '/document-generation-engine' },
    { label: 'Technical Demos', href: '/technical-demo-scheduling-system' },
    { label: 'Executive Presentations', href: '/executive-presentation-workflow' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActiveRoute = (href: string) => {
    return pathname === href;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-card transition-all duration-300 ${
        isScrolled ? 'shadow-md' : ''
      } ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link
            href="/homepage"
            className="flex items-center space-x-3 group"
            onClick={closeMobileMenu}
          >
            <div className="relative w-12 h-12 md:w-14 md:h-14 transition-transform duration-300 group-hover:scale-105">
              <Image
                src="/assets/images/ai_change_leaders_logo.png"
                alt="AI Change Leaders - Professional logo representing human-centered AI transformation"
                width={56}
                height={56}
                className="object-contain"
                priority
              />
            </div>
            <div className="flex flex-col">
              <span className="text-xl md:text-2xl font-extrabold font-headline text-navy-900 leading-tight tracking-tight">
                AI CHANGE LEADERS
              </span>
              <span className="text-xs md:text-sm text-orange-500 font-body font-semibold uppercase tracking-wide">
                AI Leadership Standards
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-4 py-2 rounded-md font-medium font-body text-sm transition-all duration-300 ${
                  isActiveRoute(item.href)
                    ? 'text-primary bg-primary/5 font-semibold' :'text-foreground hover:text-primary hover:bg-primary/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-3">
            <Link
              href="/discovery-call-booking"
              className="px-6 py-2.5 rounded-md font-semibold font-cta text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300 shadow-subtle hover:shadow-strong hover:-translate-y-0.5"
            >
              Book Discovery Call
            </Link>
            <Link
              href="/partner-network"
              className="px-6 py-2.5 rounded-md font-semibold font-cta text-sm text-primary border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
            >
              Join Network
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-foreground hover:bg-muted transition-colors duration-300"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card">
          <nav className="container mx-auto px-4 py-4 space-y-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={closeMobileMenu}
                className={`block px-4 py-3 rounded-md font-medium font-body text-base transition-all duration-300 ${
                  isActiveRoute(item.href)
                    ? 'text-primary bg-primary/5 font-semibold' :'text-foreground hover:text-primary hover:bg-primary/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-4 space-y-2">
              <Link
                href="/discovery-call-booking"
                onClick={closeMobileMenu}
                className="block w-full px-6 py-3 rounded-md font-semibold font-cta text-center text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300"
              >
                Book Discovery Call
              </Link>
              <Link
                href="/partner-network"
                onClick={closeMobileMenu}
                className="block w-full px-6 py-3 rounded-md font-semibold font-cta text-center text-sm text-primary border-2 border-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300"
              >
                Join Network
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;